/**
 * Lógica pura do modal "Editar canal" (card #255): schemas, conversão de/para a
 * API, regras da agenda de posts, diferenças para o "antes:" e o resumo do
 * cabeçalho. Fica fora dos componentes para ser testada sem DOM.
 *
 * Microcopy e regras: a tela "Anotações por aba" do canvas aprovado
 * (apps/backend/docs/plan-studio-engagement.md § "Design aprovado").
 */
import { z } from 'zod'

import type { EngagementConfigOut, EngagementPatchIn } from '@/api/endpoints/engagement'
import type {
  ChannelConfigOut,
  ChannelConfigPatchIn,
  CortesSourceIn,
  CortesSourceOut,
} from '@/api/endpoints/scheduler'
import { disabledLast, mergeWithKnownFormats } from '@/lib/videoFormats'

export type TabId = 'source' | 'generation' | 'style' | 'upload' | 'posts' | 'interactions' | 'destinations'

export const TAB_LABELS: Record<TabId, string> = {
  source: 'Origem dos vídeos',
  generation: 'Geração',
  style: 'Estilo de vídeo',
  upload: 'Upload',
  posts: 'Posts',
  interactions: 'Interações',
  destinations: 'Destinos',
}

export const NO_PRESET_VALUE = '__none__'
/** Estilos de legenda que usam a cor da palavra sendo lida (o ``random`` pode cair no karaokê). */
export const KARAOKE_STYLE = 'karaoke'
const COLOR_STYLES = new Set([KARAOKE_STYLE, 'random'])
/** Factories agendáveis que não queimam legenda: o estilo não muda nada nelas. */
export const NO_SUBTITLE_FACTORIES = new Set(['code_levels'])

/** Pedidos de engajamento que o adesivo sabe fazer, na ordem da aba (no vídeo: comentário, like, inscrição). */
export const CTA_ASKS = ['like', 'comment', 'subscribe'] as const
export type CtaAsk = (typeof CTA_ASKS)[number]
export const CTA_ASK_LABELS: Record<CtaAsk, string> = {
  like: 'Like',
  comment: 'Comentário',
  subscribe: 'Inscreva-se',
}
const CTA_DEFAULTS = { per_video: 2, share_pct: 100 }

type CtaValues = Pick<ScheduleValues, 'cta_asks' | 'cta_per_video' | 'cta_share_pct'>

/** ``video_style.engagement`` → campos do modal. O backend anterior à opção não manda o campo. */
function ctaValues(saved: { asks?: readonly string[]; per_video?: number; share_pct?: number } | undefined): CtaValues {
  return {
    cta_asks: CTA_ASKS.filter((a) => saved?.asks?.includes(a)),
    cta_per_video: saved?.per_video ?? CTA_DEFAULTS.per_video,
    cta_share_pct: saved?.share_pct ?? CTA_DEFAULTS.share_pct,
  }
}

function sameCta(a: CtaValues, b: CtaValues): boolean {
  return (
    a.cta_asks.join() === b.cta_asks.join() && a.cta_per_video === b.cta_per_video && a.cta_share_pct === b.cta_share_pct
  )
}
export const CORTES_FACTORY = 'cortes'
export const CORTES_MAX_SOURCES = 20
export const CORTES_MAX_CLIPS = 20
export const MAX_SLOTS = 10
const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/

// ---------------------------------------------------------------------------
// Geração + Upload (PATCH /scheduler/channels/{slug}/config)
// ---------------------------------------------------------------------------

export const scheduleSchema = z
  .object({
    enabled: z.boolean(),
    factory: z.string().min(1, 'Escolha uma factory'),
    generation_time: z.string().regex(TIME_REGEX, 'Use HH:MM'),
    upload_time: z.string().regex(TIME_REGEX, 'Use HH:MM'),
    videos_per_day: z.number({ error: 'De 0 a 10' }).int('Use um número inteiro').min(0, 'De 0 a 10').max(10, 'De 0 a 10'),
    // ``null`` = herda ``videos_per_day`` (o "padrão: igual à geração").
    uploads_per_day: z
      .number({ error: 'De 0 a 10' })
      .int('Use um número inteiro')
      .min(0, 'De 0 a 10')
      .max(10, 'De 0 a 10')
      .nullable(),
    preset_id: z.string(),
    use_peak_hour: z.boolean(),
    publish_window_start: z.string().regex(TIME_REGEX, 'Use HH:MM'),
    publish_window_end: z.string().regex(TIME_REGEX, 'Use HH:MM'),
    on_slot_conflict: z.enum(['skip_day', 'reschedule_next_day']),
    upload_selection_mode: z.enum(['score', 'fresh', 'priority']),
    upload_priority_types: z.array(z.string()),
    // Slugs que nem a geração nem o upload usam (``disabled_video_types``).
    disabled_video_types: z.array(z.string()),
    llm_provider: z.enum(['auto', 'local', 'cloud']),
    backlog_max_age_days: z.number({ error: 'De 1 a 30' }).int('Use um número inteiro').min(1, 'De 1 a 30').max(30, 'De 1 a 30'),
    // ``null`` = padrão do sistema (os slots e mais 1 exploratório por dia).
    max_uploads_per_cycle: z
      .number({ error: 'De 1 a 10' })
      .int('Use um número inteiro')
      .min(1, 'De 1 a 10')
      .max(10, 'De 1 a 10')
      .nullable(),
    // Aba "Estilo de vídeo" (``video_style``). Vazio = segue o preset ou o padrão global.
    subtitle_style: z.string(),
    subtitle_active_color: z.string(),
    // ``''`` = a posição que a factory escolhe; senão meio ou embaixo.
    subtitle_position: z.enum(['', 'center', 'bottom']),
    // Pedido de like/comentário/inscrição (``video_style.engagement``). Lista vazia = desligado.
    cta_asks: z.array(z.enum(CTA_ASKS)),
    cta_per_video: z.number().int().min(1).max(3),
    cta_share_pct: z.number().int().min(0).max(100),
  })
  .superRefine((v, ctx) => {
    // O backend exige a janela em ordem sempre, com o pico ligado ou não.
    if (v.publish_window_end <= v.publish_window_start) {
      ctx.addIssue({
        code: 'custom',
        path: ['publish_window_end'],
        message: `O fim precisa ser depois do início (${v.publish_window_start}).`,
      })
    }
  })

export type ScheduleValues = z.infer<typeof scheduleSchema>

/** Backend devolve ``HH:MM:SS``; os campos usam ``HH:MM``. */
export function trimSeconds(value: string | null | undefined): string {
  if (!value) return ''
  return value.length >= 5 ? value.slice(0, 5) : value
}

/**
 * ``formatFactory``: a factory cujos formatos entram na ordem de upload. É a do
 * canal, menos no canal ligado a pool, que publica o que o pool gera: lá os
 * formatos são os da factory do pool.
 */
export function toScheduleValues(config: ChannelConfigOut, formatFactory: string = config.factory): ScheduleValues {
  // O tipo diz que sempre vem, mas o backend anterior aos formatos desativados não
  // manda o campo: o Pages publica o front no merge, antes do pull no skab.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const disabled = [...new Set(config.disabled_video_types ?? [])].sort()
  return {
    enabled: config.enabled,
    factory: config.factory,
    generation_time: config.generation_time,
    upload_time: config.upload_time,
    videos_per_day: config.videos_per_day,
    uploads_per_day: config.uploads_per_day ?? null,
    preset_id: config.preset_id ?? NO_PRESET_VALUE,
    use_peak_hour: config.use_peak_hour,
    publish_window_start: trimSeconds(config.publish_window_start),
    publish_window_end: trimSeconds(config.publish_window_end),
    on_slot_conflict: config.on_slot_conflict,
    upload_selection_mode: config.upload_selection_mode,
    // Um desativado fora da ordem salva (gravado pela API) entra na lista, para dar como religá-lo.
    upload_priority_types: disabledLast(
      mergeWithKnownFormats(formatFactory, [...config.upload_priority_types, ...disabled]),
      disabled,
    ),
    disabled_video_types: disabled,
    llm_provider: config.llm_provider,
    backlog_max_age_days: config.backlog_max_age_days,
    max_uploads_per_cycle: config.max_uploads_per_cycle ?? null,
    // O backend anterior à aba não manda o campo (o Pages publica o front antes do pull).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    subtitle_style: config.video_style?.subtitle_style ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    subtitle_active_color: config.video_style?.subtitle_active_color ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    subtitle_position: config.video_style?.subtitle_position ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...ctaValues(config.video_style?.engagement),
  }
}

/** As opções de posição da legenda, na ordem da aba Estilo de vídeo. */
export const SUBTITLE_POSITIONS: ReadonlyArray<{ value: ScheduleValues['subtitle_position']; label: string }> = [
  { value: '', label: 'Padrão da factory' },
  { value: 'center', label: 'No meio' },
  { value: 'bottom', label: 'Embaixo' },
]

/** A cor só vai com um estilo que a lê; nos outros ficaria gravada à toa. */
export function usesActiveColor(style: string): boolean {
  return COLOR_STYLES.has(style)
}

/** O PATCH do modal. ``initial`` é o que veio do servidor ao abrir.
 *
 * ``disabled_video_types`` só vai quando há ou havia formato desativado. O
 * schema do PATCH recusa chave desconhecida, e no intervalo entre o merge (o
 * Pages publica o front) e o pull no skab o backend ainda não conhece o campo:
 * sem esta regra, todo Salvar daria 422 nesse intervalo, não só o de quem
 * desligou um formato. */
export function toSchedulePatch(
  values: ScheduleValues,
  slots: string[],
  initial?: Pick<ScheduleValues, 'disabled_video_types'> &
    Partial<Pick<ScheduleValues, 'subtitle_style' | 'subtitle_active_color' | 'subtitle_position'>> &
    Partial<CtaValues>,
): ChannelConfigPatchIn {
  const sendDisabled = values.disabled_video_types.length > 0 || (initial?.disabled_video_types.length ?? 0) > 0
  // ``video_style`` só vai quando muda, pela mesma razão do ``disabled_video_types``:
  // o PATCH recusa chave desconhecida, e o backend antes do pull não a conhece.
  const color = usesActiveColor(values.subtitle_style) ? values.subtitle_active_color : ''
  const initialCta: CtaValues = {
    cta_asks: initial?.cta_asks ?? [],
    cta_per_video: initial?.cta_per_video ?? CTA_DEFAULTS.per_video,
    cta_share_pct: initial?.cta_share_pct ?? CTA_DEFAULTS.share_pct,
  }
  const ctaChanged = !sameCta(values, initialCta)
  const sendStyle =
    values.subtitle_style !== (initial?.subtitle_style ?? '') ||
    color !== (initial?.subtitle_active_color ?? '') ||
    values.subtitle_position !== (initial?.subtitle_position ?? '') ||
    ctaChanged
  // A posição só vai quando está ou esteve definida: o backend de antes dela
  // recusa a chave (``extra="forbid"``), e o Pages publica o front antes do pull.
  const position =
    values.subtitle_position || initial?.subtitle_position ? { subtitle_position: values.subtitle_position } : {}
  // O ``video_style`` substitui o estilo inteiro, então o pedido vai junto da
  // legenda sempre que esteve ou está ligado. Desligado dos dois lados, ele
  // fica de fora: é o padrão, e o backend de antes da opção recusaria a chave.
  const engagement =
    ctaChanged || values.cta_asks.length > 0
      ? {
          engagement: {
            asks: values.cta_asks,
            per_video: values.cta_per_video,
            share_pct: values.cta_share_pct,
          },
        }
      : {}
  return {
    enabled: values.enabled,
    factory: values.factory,
    generation_time: values.generation_time,
    upload_time: values.upload_time,
    videos_per_day: values.videos_per_day,
    // ``null`` volta a herdar da geração — o "Voltar ao padrão".
    uploads_per_day: values.uploads_per_day,
    publish_slots: slots,
    preset_id: values.preset_id && values.preset_id !== NO_PRESET_VALUE ? values.preset_id : null,
    use_peak_hour: values.use_peak_hour,
    // A coluna é ``Time``: a API espera ``HH:MM:SS``.
    publish_window_start: `${values.publish_window_start}:00`,
    publish_window_end: `${values.publish_window_end}:00`,
    on_slot_conflict: values.on_slot_conflict,
    upload_selection_mode: values.upload_selection_mode,
    // Desativados vão para o fim ao salvar, e a lista volta assim do servidor.
    upload_priority_types: disabledLast(values.upload_priority_types, values.disabled_video_types),
    ...(sendDisabled ? { disabled_video_types: values.disabled_video_types } : {}),
    llm_provider: values.llm_provider,
    backlog_max_age_days: values.backlog_max_age_days,
    max_uploads_per_cycle: values.max_uploads_per_cycle,
    ...(sendStyle
      ? {
          // O tipo gerado exige a posição (tem default no backend), mas ela fica
          // de fora de propósito quando nunca foi escolhida: ver ``position``.
          video_style: {
            subtitle_style: values.subtitle_style,
            subtitle_active_color: color,
            ...position,
            ...engagement,
          } as NonNullable<ChannelConfigPatchIn['video_style']>,
        }
      : {}),
  }
}

export const SELECTION_MODES: ReadonlyArray<{ value: ScheduleValues['upload_selection_mode']; label: string; hint: string }> = [
  {
    value: 'score',
    label: 'Melhor nota',
    hint: 'Sobe os vídeos de maior nota, mesmo de dias anteriores. Os em quarentena ficam de fora.',
  },
  {
    value: 'fresh',
    label: 'Conteúdo fresco',
    hint:
      'Publica sempre o vídeo gerado mais recentemente, sem olhar a nota do ranking. Para canais de ' +
      'conteúdo do dia (céu de hoje, stats da rodada), em que um vídeo de três dias já perdeu a validade.',
  },
  {
    value: 'priority',
    label: 'Prioridade por formato',
    hint: 'Sobe primeiro o formato que está no topo da lista.',
  },
]

export const LLM_OPTIONS: ReadonlyArray<{ value: ScheduleValues['llm_provider']; label: string }> = [
  { value: 'auto', label: 'Padrão (global)' },
  { value: 'cloud', label: 'Nuvem · Claude/Gemini' },
  { value: 'local', label: 'Local · Ollama' },
]

export const CONFLICT_OPTIONS: ReadonlyArray<{ value: ScheduleValues['on_slot_conflict']; label: string; hint: string }> = [
  {
    value: 'reschedule_next_day',
    label: 'Reagendar para o próximo dia',
    hint: 'Empurra dia a dia até achar um slot livre.',
  },
  {
    value: 'skip_day',
    label: 'Pular o canal hoje',
    hint: 'Os vídeos ficam no backlog para o próximo ciclo.',
  },
]

// ---------------------------------------------------------------------------
// Fontes do Cortes
// ---------------------------------------------------------------------------

export interface SourceRow {
  key: number
  url: string
  alias: string
  max_clips: number
  enabled: boolean
}

export function sourceRowFromApi(it: CortesSourceOut, key: number): SourceRow {
  return { key, url: it.url, alias: it.alias, max_clips: it.max_clips, enabled: it.enabled }
}

/** Fonte nova: ativa. */
export function newSourceRow(key: number): SourceRow {
  return { key, url: '', alias: '', max_clips: 5, enabled: true }
}

/** Corpo do PUT das fontes. Não manda `gameplay_layout`: o layout de gameplay
 * é automático desde a fase 4 do card #391 (docs/plan-cortes-gameplay-layout.md),
 * e a fase 5 tirou o campo da API (com ele, o PUT responde 422). */
export function toSourcePayload(rows: ReadonlyArray<SourceRow>): CortesSourceIn[] {
  return rows.map((r) => ({
    url: r.url.trim(),
    alias: r.alias.trim(),
    max_clips: r.max_clips,
    enabled: r.enabled,
  }))
}

/** Espelho da validação do backend, para o operador ver antes de salvar. */
export function validateSources(rows: ReadonlyArray<SourceRow>): string | null {
  if (rows.length > CORTES_MAX_SOURCES) return `Máximo ${String(CORTES_MAX_SOURCES)} fontes por canal`
  const urls = new Set<string>()
  const aliases = new Set<string>()
  for (const row of rows) {
    const url = row.url.trim()
    const alias = row.alias.trim()
    const who = alias || url || '(vazia)'
    if (!/^https?:\/\//.test(url)) return `Fonte "${who}": a URL precisa começar com http:// ou https://`
    if (!alias) return `Fonte ${url}: informe um apelido`
    if (!Number.isInteger(row.max_clips) || row.max_clips < 1 || row.max_clips > CORTES_MAX_CLIPS) {
      return `Fonte "${alias}": máx. de cortes deve ficar entre 1 e ${String(CORTES_MAX_CLIPS)}`
    }
    if (urls.has(url)) return `URL repetida: ${url}`
    if (aliases.has(alias.toLowerCase())) return `Apelido repetido: ${alias}`
    urls.add(url)
    aliases.add(alias.toLowerCase())
  }
  return null
}

export function sameSources(a: ReadonlyArray<SourceRow>, b: ReadonlyArray<SourceRow>): boolean {
  const strip = (rows: ReadonlyArray<SourceRow>) =>
    JSON.stringify(
      rows.map(({ url, alias, max_clips, enabled }) => [url.trim(), alias.trim(), max_clips, enabled]),
    )
  return strip(a) === strip(b)
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

/** Tenta incluir ``value``: ordena, recusa duplicado e respeita o limite. */
export function addSlot(slots: readonly string[], raw: string): { slots: string[]; error: string | null } {
  const value = raw.trim()
  if (!TIME_REGEX.test(value)) return { slots: [...slots], error: 'Use HH:MM (ex.: 09:30).' }
  if (slots.includes(value)) return { slots: [...slots], error: `${value} já está na lista.` }
  if (slots.length >= MAX_SLOTS) return { slots: [...slots], error: `Máximo ${String(MAX_SLOTS)} slots por canal.` }
  return { slots: [...slots, value].sort(), error: null }
}

// ---------------------------------------------------------------------------
// Posts e Interações (PATCH /engagement/channels/{slug})
// ---------------------------------------------------------------------------

export const POST_TYPES = ['poll', 'open_question', 'teaser', 'trending'] as const
export type PostType = (typeof POST_TYPES)[number]
export type Priority = 1 | 2 | 3

export const POST_TYPE_INFO: Record<PostType, { label: string; hint: string; short: string; aria: string }> = {
  poll: { label: 'Enquete', hint: 'Até 4 opções, só texto.', short: 'Até 4 opções, só texto.', aria: 'da enquete' },
  open_question: {
    label: 'Pergunta aberta',
    hint: 'Convida o público a responder nos comentários.',
    short: 'Convida a comentar.',
    aria: 'da pergunta aberta',
  },
  teaser: {
    label: 'Teaser do vídeo do dia',
    hint: 'Chama para o Short que sai no mesmo dia.',
    short: 'Chama para o Short do dia.',
    aria: 'do teaser',
  },
  trending: {
    label: 'Tendência do nicho',
    hint: 'Comenta um assunto em alta no nicho do canal.',
    short: 'Assunto em alta no nicho.',
    aria: 'da tendência do nicho',
  },
}

export const PRIORITY_LABELS: Record<Priority, string> = { 1: 'Baixa', 2: 'Normal', 3: 'Alta' }

const priority = z.union([z.literal(1), z.literal(2), z.literal(3)])
const typeEntry = z.object({ enabled: z.boolean(), priority })

export const engagementSchema = z
  .object({
    posts_enabled: z.boolean(),
    posts_days: z.array(z.number().int().min(1).max(7)),
    posts_time_mode: z.enum(['best', 'fixed']),
    posts_time: z.string(),
    posts_types: z.object({ poll: typeEntry, open_question: typeEntry, teaser: typeEntry, trending: typeEntry }),
    posts_milestone_enabled: z.boolean(),
    posts_require_approval: z.boolean(),
    hearts_enabled: z.boolean(),
    hearts_per_day: z
      .number({ error: 'De 1 a 10' })
      .int('Use um número inteiro')
      .min(1, 'Com o toggle ligado, o mínimo é 1.')
      .max(10, 'De 1 a 10'),
    hearts_window_hours: z.union([z.literal(24), z.literal(48), z.literal(72)]),
    hearts_priority: z.enum(['questions', 'likes', 'recent']),
    hearts_start: z.string().regex(TIME_REGEX, 'Use HH:MM'),
    hearts_end: z.string().regex(TIME_REGEX, 'Use HH:MM'),
    hearts_debate_mode: z.boolean(),
    pin_enabled: z.boolean(),
    pin_mode: z.enum(['publish', 'after']),
    // Só vale com "Horas depois" (checado abaixo): com "Ao publicar" nem é enviado.
    pin_delay_hours: z.union([z.number(), z.nan()]),
  })
  .superRefine((v, ctx) => {
    if (v.posts_enabled && v.posts_days.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['posts_days'], message: 'Marque pelo menos 1 dia.' })
    }
    if (v.posts_enabled && v.posts_time_mode === 'fixed') {
      if (!TIME_REGEX.test(v.posts_time)) {
        ctx.addIssue({ code: 'custom', path: ['posts_time'], message: 'Use HH:MM' })
      } else if (Number(v.posts_time.slice(3)) % 15 !== 0) {
        // O painel do YouTube só programa post de 15 em 15 minutos.
        ctx.addIssue({ code: 'custom', path: ['posts_time'], message: 'Use um horário de 15 em 15 minutos' })
      }
    }
    if (v.posts_enabled && !POST_TYPES.some((t) => v.posts_types[t].enabled)) {
      ctx.addIssue({ code: 'custom', path: ['posts_types'], message: 'Ligue pelo menos um tipo de post.' })
    }
    if (
      v.pin_mode === 'after' &&
      (!Number.isInteger(v.pin_delay_hours) || v.pin_delay_hours < 1 || v.pin_delay_hours > 72)
    ) {
      ctx.addIssue({ code: 'custom', path: ['pin_delay_hours'], message: 'De 1 a 72' })
    }
    if (v.hearts_end <= v.hearts_start) {
      ctx.addIssue({
        code: 'custom',
        path: ['hearts_end'],
        message: `O fim precisa ser depois do início (${v.hearts_start}).`,
      })
    }
  })

export type EngagementValues = z.infer<typeof engagementSchema>

function toPriority(weight: number | undefined): Priority {
  if (!weight || weight <= 1) return 1
  return weight >= 3 ? 3 : 2
}

export function toEngagementValues(cfg: EngagementConfigOut): EngagementValues {
  const weights = cfg.posts_types
  const entry = (t: PostType) => {
    const w = weights[t] ?? 0
    return { enabled: w > 0, priority: w > 0 ? toPriority(w) : (2 as Priority) }
  }
  return {
    posts_enabled: cfg.posts_enabled,
    posts_days: [...cfg.posts_days].sort((a, b) => a - b),
    posts_time_mode: cfg.posts_time ? 'fixed' : 'best',
    posts_time: cfg.posts_time ?? '19:00',
    posts_types: {
      poll: entry('poll'),
      open_question: entry('open_question'),
      teaser: entry('teaser'),
      trending: entry('trending'),
    },
    posts_milestone_enabled: cfg.posts_milestone_enabled,
    posts_require_approval: cfg.posts_require_approval,
    hearts_enabled: cfg.hearts_enabled,
    hearts_per_day: cfg.hearts_per_day,
    hearts_window_hours: cfg.hearts_window_hours as 24 | 48 | 72,
    hearts_priority: cfg.hearts_priority,
    hearts_start: trimSeconds(cfg.hearts_start),
    hearts_end: trimSeconds(cfg.hearts_end),
    // O tipo diz que sempre vem, mas o backend anterior ao modo debate não manda o
    // campo: o Pages publica o front no merge, antes do pull no skab. Sem o ``??``,
    // o ``undefined`` reprovaria no schema e travaria o Salvar do modal inteiro.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    hearts_debate_mode: cfg.hearts_debate_mode ?? false,
    pin_enabled: cfg.pin_enabled,
    pin_mode: cfg.pin_delay_hours > 0 ? 'after' : 'publish',
    pin_delay_hours: cfg.pin_delay_hours > 0 ? cfg.pin_delay_hours : 2,
  }
}

export function typeWeights(types: EngagementValues['posts_types']): Record<PostType, number> {
  return {
    poll: types.poll.enabled ? types.poll.priority : 0,
    open_question: types.open_question.enabled ? types.open_question.priority : 0,
    teaser: types.teaser.enabled ? types.teaser.priority : 0,
    trending: types.trending.enabled ? types.trending.priority : 0,
  }
}

export function toEngagementPatch(v: EngagementValues): EngagementPatchIn {
  return {
    posts_enabled: v.posts_enabled,
    posts_per_week: postsPerWeekOf(v.posts_days),
    posts_days: v.posts_days,
    posts_time: v.posts_time_mode === 'fixed' ? v.posts_time : null,
    posts_types: typeWeights(v.posts_types),
    posts_milestone_enabled: v.posts_milestone_enabled,
    posts_require_approval: v.posts_require_approval,
    hearts_enabled: v.hearts_enabled,
    hearts_per_day: v.hearts_per_day,
    hearts_window_hours: v.hearts_window_hours,
    hearts_priority: v.hearts_priority,
    hearts_start: `${v.hearts_start}:00`,
    hearts_end: `${v.hearts_end}:00`,
    hearts_debate_mode: v.hearts_debate_mode,
    pin_enabled: v.pin_enabled,
    pin_delay_hours: v.pin_mode === 'publish' ? 0 : v.pin_delay_hours,
  }
}

// ---------------------------------------------------------------------------
// Dias da semana
// ---------------------------------------------------------------------------

/** Ordem do seletor (domingo primeiro), em dias ISO (1 = segunda … 7 = domingo). */
export const DAY_ORDER = [7, 1, 2, 3, 4, 5, 6] as const
export const DAY_LABELS: Record<number, string> = { 1: 'Seg', 2: 'Ter', 3: 'Qua', 4: 'Qui', 5: 'Sex', 6: 'Sáb', 7: 'Dom' }
const DAY_SHORT: Record<number, string> = { 1: 'seg', 2: 'ter', 3: 'qua', 4: 'qui', 5: 'sex', 6: 'sáb', 7: 'dom' }

/**
 * Cada dia marcado recebe 1 post: o ``posts_per_week`` que a API guarda é derivado dos
 * dias (mínimo 1, que o backend exige mesmo com o toggle desligado e nenhum dia).
 */
export function postsPerWeekOf(days: readonly number[]): number {
  return Math.max(1, new Set(days).size)
}

// ---------------------------------------------------------------------------
// Datas
// ---------------------------------------------------------------------------

const WEEKDAY_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/** "hoje 04:00", "amanhã 00:00", "ontem 21:30" ou "sex 09/10 14:00". */
export function relativeWhen(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(d, now)) return `hoje ${hm}`
  if (sameDay(d, tomorrow)) return `amanhã ${hm}`
  if (sameDay(d, yesterday)) return `ontem ${hm}`
  return `${WEEKDAY_SHORT[d.getDay()] ?? ''} ${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${hm}`
}

/** "ter 13/10" — o dia de uma linha da prévia. */
export function dayLabel(iso: string): string {
  const d = new Date(iso)
  return `${WEEKDAY_SHORT[d.getDay()] ?? ''} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
}

export function clockLabel(iso: string): string {
  const d = new Date(iso)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ---------------------------------------------------------------------------
// Resumo do cabeçalho
// ---------------------------------------------------------------------------

export interface SummaryInput {
  enabled: boolean
  platform: 'youtube' | 'tiktok'
  nextGeneration?: string | null
  nextUpload?: string | null
  postsEnabled: boolean
  postsPerWeek: number
  heartsEnabled: boolean
  heartsPerDay: number
  nextScheduledPost?: string | null
}

/** As partes da linha-resumo, na versão longa (desktop) e curta (celular). */
export function summaryParts(input: SummaryInput, now: Date = new Date()): { long: string[]; short: string[] } {
  if (!input.enabled) {
    const tail = input.nextScheduledPost
      ? `post já programado: ${relativeWhen(input.nextScheduledPost, now)}`
      : 'Nada novo é gerado ou publicado'
    return { long: ['Pausado', tail], short: ['Pausado', tail] }
  }
  const long: string[] = []
  const short: string[] = []
  if (input.nextGeneration) {
    long.push(`Próxima geração ${relativeWhen(input.nextGeneration, now)}`)
    short.push(`Geração ${relativeWhen(input.nextGeneration, now)}`)
  }
  if (input.nextUpload) {
    long.push(`próximo upload ${relativeWhen(input.nextUpload, now)}`)
    short.push(`upload ${relativeWhen(input.nextUpload, now)}`)
  }
  if (input.platform === 'youtube') {
    if (input.postsEnabled) {
      long.push(`${String(input.postsPerWeek)} ${input.postsPerWeek === 1 ? 'post' : 'posts'} por semana`)
      short.push(`${String(input.postsPerWeek)} posts/sem.`)
    }
    if (input.heartsEnabled) {
      long.push(`até ${String(input.heartsPerDay)} ${input.heartsPerDay === 1 ? 'coração' : 'corações'} por dia`)
      short.push(`até ${String(input.heartsPerDay)} coração/dia`)
    }
  }
  return { long, short }
}

// ---------------------------------------------------------------------------
// Alterações
// ---------------------------------------------------------------------------

export interface Change {
  field: string
  tab: TabId | 'header'
  label: string
  before: string
  after: string
}

type Formatter = (value: unknown) => string

export interface FieldSpec {
  tab: TabId | 'header'
  label: string
  format?: Formatter
}

const yesNo: Formatter = (v) => (v ? 'ligado' : 'desligado')
const plain: Formatter = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : JSON.stringify(v)
}

export const SCHEDULE_FIELDS: Record<keyof ScheduleValues, FieldSpec> = {
  enabled: { tab: 'header', label: 'Ativo', format: (v) => (v ? 'ativo' : 'pausado') },
  factory: { tab: 'generation', label: 'Factory' },
  generation_time: { tab: 'generation', label: 'Horário de geração' },
  videos_per_day: { tab: 'generation', label: 'Vídeos por dia (geração)' },
  preset_id: { tab: 'generation', label: 'Preset', format: (v) => (v === NO_PRESET_VALUE ? 'Sem preset' : plain(v)) },
  llm_provider: {
    tab: 'generation',
    label: 'Modelo de IA',
    format: (v) => LLM_OPTIONS.find((o) => o.value === v)?.label ?? plain(v),
  },
  upload_selection_mode: {
    tab: 'upload',
    label: 'Critério de escolha',
    format: (v) => SELECTION_MODES.find((o) => o.value === v)?.label ?? plain(v),
  },
  upload_priority_types: { tab: 'upload', label: 'Ordem dos formatos', format: () => 'reordenada' },
  disabled_video_types: {
    tab: 'upload',
    label: 'Formatos desativados',
    // Contagem, não nomes: o "antes:" divide a linha com o rótulo da lista, e no
    // celular sete nomes não cabem. Os nomes estão na própria lista.
    format: (v) => {
      const n = Array.isArray(v) ? v.length : 0
      if (n === 0) return 'nenhum desativado'
      return n === 1 ? '1 desativado' : `${String(n)} desativados`
    },
  },
  upload_time: { tab: 'upload', label: 'Horário do ciclo de upload' },
  uploads_per_day: {
    tab: 'upload',
    label: 'Vídeos por dia (upload)',
    format: (v) => (v === null ? 'padrão' : plain(v)),
  },
  on_slot_conflict: {
    tab: 'upload',
    label: 'Dia com uploads programados',
    format: (v) => CONFLICT_OPTIONS.find((o) => o.value === v)?.label ?? plain(v),
  },
  use_peak_hour: { tab: 'upload', label: 'Upload dinâmico pelo pico', format: yesNo },
  publish_window_start: { tab: 'upload', label: 'Início da janela' },
  publish_window_end: { tab: 'upload', label: 'Fim da janela' },
  backlog_max_age_days: { tab: 'upload', label: 'Idade máxima do backlog' },
  max_uploads_per_cycle: {
    tab: 'upload',
    label: 'Máx. de uploads por ciclo',
    format: (v) => (v === null ? 'padrão' : plain(v)),
  },
  subtitle_style: {
    tab: 'style',
    label: 'Estilo de legenda',
    format: (v) => (v === '' ? 'padrão' : v === 'random' ? 'aleatório' : plain(v)),
  },
  subtitle_active_color: {
    tab: 'style',
    label: 'Cor da palavra sendo lida',
    format: (v) => (v === '' ? 'vermelho (padrão)' : plain(v)),
  },
  subtitle_position: {
    tab: 'style',
    label: 'Posição da legenda',
    format: (v) => SUBTITLE_POSITIONS.find((o) => o.value === v)?.label ?? plain(v),
  },
  cta_asks: {
    tab: 'style',
    label: 'Pedidos de engajamento',
    format: (v) =>
      Array.isArray(v) && v.length ? (v as CtaAsk[]).map((a) => CTA_ASK_LABELS[a]).join(', ') : 'desligado',
  },
  cta_per_video: { tab: 'style', label: 'Pedidos por vídeo' },
  cta_share_pct: { tab: 'style', label: 'Vídeos com pedido', format: (v) => `${plain(v)}%` },
}

const dayList: Formatter = (v) =>
  Array.isArray(v) && v.length ? (v as number[]).map((d) => DAY_SHORT[d] ?? String(d)).join(', ') : 'nenhum'

export const ENGAGEMENT_FIELDS: Record<keyof EngagementValues, FieldSpec> = {
  posts_enabled: { tab: 'posts', label: 'Posts automáticos', format: yesNo },
  posts_days: { tab: 'posts', label: 'Dias da semana', format: dayList },
  posts_time_mode: {
    tab: 'posts',
    label: 'Horário dos posts',
    format: (v) => (v === 'best' ? 'melhor horário' : 'horário fixo'),
  },
  posts_time: { tab: 'posts', label: 'Horário fixo' },
  posts_types: { tab: 'posts', label: 'Mistura de tipos', format: () => 'alterada' },
  posts_milestone_enabled: { tab: 'posts', label: 'Marcos de inscritos', format: yesNo },
  posts_require_approval: {
    tab: 'posts',
    label: 'Modo',
    format: (v) => (v ? 'com aprovação' : 'automático'),
  },
  hearts_enabled: { tab: 'interactions', label: 'Coração do criador', format: yesNo },
  hearts_per_day: { tab: 'interactions', label: 'Corações por dia' },
  hearts_window_hours: { tab: 'interactions', label: 'Comentários das últimas', format: (v) => `${plain(v)} h` },
  hearts_priority: {
    tab: 'interactions',
    label: 'Priorizar',
    format: (v) => ({ questions: 'perguntas', likes: 'mais curtidos', recent: 'mais recentes' })[v as string] ?? plain(v),
  },
  hearts_start: { tab: 'interactions', label: 'Início da janela do coração' },
  hearts_end: { tab: 'interactions', label: 'Fim da janela do coração' },
  hearts_debate_mode: { tab: 'interactions', label: 'Canal de debate político', format: yesNo },
  pin_enabled: { tab: 'interactions', label: 'Comentário fixado', format: yesNo },
  pin_mode: {
    tab: 'interactions',
    label: 'Quando fixar',
    format: (v) => (v === 'publish' ? 'ao publicar' : 'horas depois'),
  },
  pin_delay_hours: { tab: 'interactions', label: 'Horas depois da publicação', format: (v) => `${plain(v)} h` },
}

function equal(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** As alterações de ``current`` sobre ``initial``, com o rótulo e o antes/depois formatados. */
export function diffValues<T extends object>(
  initial: T,
  current: T,
  specs: Record<keyof T, FieldSpec>,
  ignore: ReadonlyArray<keyof T> = [],
): Change[] {
  const out: Change[] = []
  for (const key of Object.keys(specs) as Array<keyof T>) {
    if (ignore.includes(key)) continue
    const before = initial[key]
    const after = current[key]
    if (equal(before, after)) continue
    const spec = specs[key]
    const fmt = spec.format ?? plain
    out.push({ field: String(key), tab: spec.tab, label: spec.label, before: fmt(before), after: fmt(after) })
  }
  return out
}

/** Os campos que não valem para a comparação no estado atual (ex.: horário fixo com "melhor horário"). */
export function inactiveEngagementFields(v: EngagementValues): Array<keyof EngagementValues> {
  const out: Array<keyof EngagementValues> = []
  if (v.posts_time_mode === 'best') out.push('posts_time')
  if (v.pin_mode === 'publish') out.push('pin_delay_hours')
  return out
}

/** "Geração, Upload" — as abas (e o Ativo) que têm alteração, na ordem das abas. */
export function changedAreas(changes: readonly Change[]): string[] {
  const order: Array<TabId | 'header'> = ['header', 'source', 'generation', 'style', 'upload', 'posts', 'interactions', 'destinations']
  const tabs = new Set(changes.map((c) => c.tab))
  return order.filter((t) => tabs.has(t)).map((t) => (t === 'header' ? 'Ativo' : TAB_LABELS[t]))
}

/** Aba de cada campo com erro de validação. */
export function tabOfField(field: string): TabId | 'header' {
  const key = field.split('.')[0] as keyof ScheduleValues & keyof EngagementValues
  return (SCHEDULE_FIELDS as Record<string, FieldSpec>)[key]?.tab ?? (ENGAGEMENT_FIELDS as Record<string, FieldSpec>)[key]?.tab ?? 'generation'
}
